/** @format */

import {
	ChangeDetectionStrategy,
	Component,
	Signal,
	computed,
	inject,
	input,
	output
} from '@angular/core';
import {
	CompareBarProduct,
	CompareProductDetailsError,
	CompareProductItem,
	PriceRange
} from '@compare/data-access';
import {
	HmfAlertBannerComponent,
	HmfBadgeComponent,
	HmfButtonComponent,
	HmfPriceComponent,
	HmfPrices,
	HmfProductImageComponent,
	HmfStarRatingsComponent
} from '@dsg-tech/homefield-angular';
import {
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService,
	homefieldIconDeleteFilled
} from '@dsg-tech/homefield-angular-icons';
import { UtilityService, WINDOW } from '@shared/data-access/services';

import { CommonModule } from '@angular/common';
import { SkeletonCompareGridProductCardComponent } from '../skeleton-compare-grid-product-card/skeleton-compare-grid-product-card.component';

@Component({
	selector: 'compare-components-compare-grid-product-card',
	imports: [
		CommonModule,
		HmfAlertBannerComponent,
		HmfBadgeComponent,
		HmfButtonComponent,
		HmfPriceComponent,
		HmfProductImageComponent,
		HomefieldIconLibraryModule,
		HmfStarRatingsComponent,
		SkeletonCompareGridProductCardComponent
	],
	templateUrl: './compare-grid-product-card.component.html',
	styleUrl: './compare-grid-product-card.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompareGridProductCardComponent {
	private readonly window = inject<Window>(WINDOW);
	private readonly hmfIconService = inject(HomefieldIconsLibraryService);
	private readonly utilityService = inject(UtilityService);

	readonly isCompact = input.required<boolean>();
	readonly productDetailsData = input.required<CompareProductItem | null>();
	readonly compareBarProductData = input.required<CompareBarProduct>();
	readonly showRemoveButton = input.required<boolean>();

	readonly removeButtonClick = output<string>();

	readonly isLoading = computed(() => !this.productDetailsData());
	readonly productErrors: Signal<CompareProductDetailsError[]> = computed(() =>
		this.checkForProductErrors(this.productDetailsData())
	);
	readonly prices: Signal<HmfPrices | null> = computed(() => {
		const errors = this.productErrors();
		if (
			!this.productDetailsData() ||
			errors.includes('noStyle') ||
			errors.includes('noPrices')
		) {
			return null;
		}

		return this.convertPriceRangeTypeToHmfPrices(this.productDetailsData().style.prices);
	});
	readonly ratingValue: Signal<number | null> = computed(() => {
		const errors = this.productErrors();
		if (!this.productDetailsData() || errors.includes('noStyle')) {
			return null;
		}
		const val = this.productDetailsData()?.style?.ratingValue ?? 0;
		const roundedRating = val.toFixed(1);
		return Number(roundedRating);
	});
	readonly ratingCount: Signal<number | null> = computed(() => {
		const errors = this.productErrors();
		if (!this.productDetailsData() || errors.includes('noStyle')) {
			return null;
		}
		return this.productDetailsData()?.style?.ratingCount || 0;
	});
	readonly badgeName: Signal<string | null> = computed(() => {
		const errors = this.productErrors();
		const compareSource = this.compareBarProductData().compareSource;
		if (!this.productDetailsData() || errors.includes('noStyle')) {
			return null;
		}
		// "Viewing Item" badge on PDP
		if (compareSource === 'currentProduct') {
			return 'Viewing Item';
		}
		return this.productDetailsData()?.style?.badge || null;
	});
	readonly productNameAriaLabel: Signal<string | null> = computed(() => {
		const errors = this.productErrors();
		if (
			!this.productDetailsData() ||
			errors.includes('noStyle') ||
			errors.includes('noPrices')
		) {
			return null;
		}

		const pricingString = this.getPricingString(this.productDetailsData().style.prices);
		return `${this.compareBarProductData().name}. ${pricingString}`;
	});
	readonly reviewStarsAriaLabel: Signal<string | null> = computed(() => {
		const errors = this.productErrors();
		if (
			!this.productDetailsData() ||
			errors.includes('noStyle') ||
			errors.includes('noPrices')
		) {
			return null;
		}

		return this.getReviewStarsAriaLabel(this.ratingCount(), this.ratingValue());
	});

	constructor() {
		this.hmfIconService.registerIcons([homefieldIconDeleteFilled]);
	}

	private convertPriceRangeTypeToHmfPrices(prices: PriceRange): HmfPrices {
		return {
			...prices,
			maxListPrice: prices.maxlistprice,
			maxOfferPrice: prices.maxofferprice,
			minListPrice: prices.minlistprice,
			minOfferPrice: prices.minofferprice
		};
	}

	goToProductDetailsPage(event: KeyboardEvent): void {
		event.preventDefault();
		this.window.location.href = this.compareBarProductData().pdpURL;
	}

	removeProductFromCompareGrid(): void {
		this.removeButtonClick.emit(this.compareBarProductData().ecode);
	}

	private checkForProductErrors(
		productDetailsData: CompareProductItem | null
	): CompareProductDetailsError[] {
		if (!productDetailsData) {
			return [];
		}

		const errors = [];

		if (!productDetailsData.style) {
			errors.push('noStyle');
		}

		if (!productDetailsData.style?.prices) {
			errors.push('noPrices');
		}

		return errors;
	}

	private getPricingString(prices: PriceRange): string {
		let newPriceString = '';

		// Full Price - no Price In Cart (PiC) nor previous or 'was' price (WAS)
		if (prices.priceIndicator === 1 && prices.mapPriceIndicator === 0) {
			newPriceString = `$${prices.minofferprice}`;

			if (prices.minofferprice !== prices.maxofferprice) {
				newPriceString += ` to $${prices.maxofferprice}`;
			}
		}

		// Sale Price - WAS price present, no PiC
		if (prices.priceIndicator === 0 && prices.mapPriceIndicator === 0) {
			let lowerPrice = `New Lower Price: $${prices.minofferprice}`;

			if (prices.minofferprice !== prices.maxofferprice) {
				lowerPrice += ` to $${prices.maxofferprice}`;
			}

			let wasPrice = `Previous Price: $${prices.minlistprice}`;

			if (prices.minlistprice !== prices.maxlistprice) {
				wasPrice += ` to $${prices.maxlistprice}`;
			}

			newPriceString = `${lowerPrice} , ${wasPrice}`;
		}

		// PiC and WAS
		if (prices.mapPriceIndicator === 1) {
			newPriceString = `See Price in Cart. Previous Price: $${prices.minlistprice}`;

			if (prices.minlistprice !== prices.maxlistprice) {
				newPriceString += ` to $${prices.maxlistprice}`;
			}
		}

		return newPriceString;
	}

	private getReviewStarsAriaLabel(ratingCount: number, ratingValue: number): string | null {
		if (!ratingCount || !ratingValue) {
			return null;
		}

		// ratingValue % 1 !== 0 tells if the number has decimals or not
		return `${ratingValue % 1 !== 0 ? ratingValue.toFixed(1) : ratingValue} out of 5 stars based on ${ratingCount} reviews`;
	}

	scrollToTop() {
		this.utilityService.scrollToTop();
	}
}
